import { useDispatch } from "react-redux";
/**
 * プレイヤーアイコン編集モーダル
 *
 * 保護者ページのプレイヤーアイコン変更専用
 * CreateUserWizardのアイコン選択と同じUI
 */
import { useState, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import { Dialog } from "@mui/material";
import { AppDispatch } from "app/store";
import { btnDelay, useScale, usePlaySound } from "common/utils";
import { ParentCloseBtn, SpinModal, FadeTransition, UserAvatar } from "common/elements";
import I18n from "features/parent/i18n";
import { updateUser } from "features/user/api";
import { GenerationStatus } from "features/generation/types";
import { CircularProgress } from "features/generation/components";
import { useAIImageGeneration } from "features/generation/hooks/useAIImageGeneration";
import { COLOR_PALETTE, generateColorVariations, BaseColorKey } from "features/user/constants";
import { ProfileAIStep } from "./ProfileAIStep";
import { isValidBirthday } from "./ProfileAIStep/BirthdayStep";
import { IconMethodStep, PresetIconStep, UploadIconStep } from "./steps";
import {
  PlayerIconEditorProps,
  EditorState,
  EditorStep,
  initialState,
  ProfileData,
  ProfileSubStep,
  FavoritesSubStep,
  EditorProfileState,
  ChildGender,
} from "./types";

const PANEL_WIDTH = 950;
const PANEL_HEIGHT = 950;

/**
 * 保存された色からベース色とバリエーション色を逆算する
 */
const resolveFavoriteColorState = (favoriteColors?: string[] | null) => {
  if (!favoriteColors || favoriteColors.length === 0) {
    return { selectedBaseColors: [] as BaseColorKey[], favoriteColors: [] as string[] };
  }

  const savedColor = favoriteColors[0];
  if (!savedColor) {
    return { selectedBaseColors: [] as BaseColorKey[], favoriteColors: [] as string[] };
  }

  const normalizedColor = savedColor.toUpperCase();

  for (const palette of COLOR_PALETTE) {
    if (
      palette.key.toUpperCase() === normalizedColor ||
      palette.hex.toUpperCase() === normalizedColor
    ) {
      const variations = generateColorVariations(palette.key);
      const matchedVariation = variations.find((hex) => hex.toUpperCase() === normalizedColor);
      return {
        selectedBaseColors: [palette.key],
        favoriteColors: matchedVariation
          ? [matchedVariation]
          : variations[2]
            ? [variations[2]]
            : [savedColor],
      };
    }

    const variations = generateColorVariations(palette.key);
    const matched = variations.find((hex) => hex.toUpperCase() === normalizedColor);
    if (matched) {
      return {
        selectedBaseColors: [palette.key],
        favoriteColors: [matched],
      };
    }
  }

  return { selectedBaseColors: [] as BaseColorKey[], favoriteColors };
};

/**
 * プロフィールが完全かどうかを判定
 * すべての必須項目が入力されているかチェック
 */
const isProfileComplete = (state: EditorProfileState): boolean => {
  // gender
  if (!state.gender || state.gender === ChildGender.NONE) return false;
  // birthday（有効な日付）
  if (!isValidBirthday(state.birthday)) return false;
  // appearance（4項目すべて）
  if (!state.accessory || state.accessory.length === 0) return false;
  if (!state.hairstyle) return false;
  if (!state.bodyType) return false;
  if (!state.faceShape) return false;
  // favoriteColors
  if (!state.favoriteColors || state.favoriteColors.length === 0) return false;
  // bestSubjects
  if (!state.bestSubjects || state.bestSubjects.length === 0) return false;

  return true;
};

/**
 * 最初の欠けているステップを見つける
 * プロフィールが不完全な場合、どのステップから入力を開始すべきか判定
 */
const getFirstMissingStep = (
  state: EditorProfileState,
): { profileSubStep: ProfileSubStep; favoritesSubStep?: FavoritesSubStep } => {
  // 1. gender
  if (!state.gender || state.gender === ChildGender.NONE) {
    return { profileSubStep: ProfileSubStep.GENDER };
  }
  // 2. birthday
  if (!isValidBirthday(state.birthday)) {
    return { profileSubStep: ProfileSubStep.BIRTHDAY };
  }
  // 3. appearance（4項目すべて必須）
  if (
    !state.accessory ||
    state.accessory.length === 0 ||
    !state.hairstyle ||
    !state.bodyType ||
    !state.faceShape
  ) {
    return {
      profileSubStep: ProfileSubStep.FAVORITES,
      favoritesSubStep: FavoritesSubStep.APPEARANCE,
    };
  }
  // 4. color
  if (!state.favoriteColors || state.favoriteColors.length === 0) {
    return { profileSubStep: ProfileSubStep.FAVORITES, favoritesSubStep: FavoritesSubStep.COLOR };
  }
  // 5. bestSubject
  if (!state.bestSubjects || state.bestSubjects.length === 0) {
    return {
      profileSubStep: ProfileSubStep.FAVORITES,
      favoritesSubStep: FavoritesSubStep.BEST_SUBJECT,
    };
  }
  // すべて完了
  return { profileSubStep: ProfileSubStep.ICON_PREVIEW };
};

/**
 * 入力済みステップのIDリストを取得
 */
const getCompletedStepIds = (state: EditorProfileState): string[] => {
  const ids: string[] = [];
  if (state.gender === "male" || state.gender === "female") ids.push("gender");
  if (isValidBirthday(state.birthday)) ids.push("birthday");
  if (
    state.accessory &&
    state.accessory.length > 0 &&
    state.hairstyle &&
    state.bodyType &&
    state.faceShape
  )
    ids.push("appearance");
  if (state.favoriteColors.length > 0) ids.push("color");
  if (state.bestSubjects.length > 0) ids.push("bestSubject");
  return ids;
};

export const PlayerIconEditor = ({
  open,
  onClose,
  targetUid,
  currentIcon,
  currentGender,
  currentBirthday,
  currentFavoriteColor,
  currentBestSubject,
  currentSelfIntroduction,
  currentAccessory,
  currentHairstyle,
  currentBodyType,
  currentFaceShape,
  onSelect,
}: PlayerIconEditorProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch<AppDispatch>();

  const [currentStep, setCurrentStep] = useState<EditorStep>(EditorStep.ICON_METHOD);
  const [state, setState] = useState<EditorState>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ProfileAI用のサブステップ管理
  const [profileSubStep, setProfileSubStep] = useState<ProfileSubStep>(ProfileSubStep.GENDER);
  const [favoritesSubStep, setFavoritesSubStep] = useState<FavoritesSubStep>(
    FavoritesSubStep.APPEARANCE,
  );
  const [additionalPrompt, setAdditionalPrompt] = useState<string>("");
  // 直接ICON_PREVIEWに遷移したかどうか（サブステップバーと戻るボタンの表示制御用）
  const [skippedToPreview, setSkippedToPreview] = useState(false);

  // プロフィール状態（既存情報から初期化）
  const [profileState, setProfileState] = useState<EditorProfileState>(() => {
    const parsedDate =
      currentBirthday && isValidBirthday(currentBirthday) ? dayjs(currentBirthday) : null;
    return {
      gender: (currentGender as EditorProfileState["gender"]) || null,
      birthday: parsedDate ? parsedDate.format("YYYY-MM-DD") : "",
      birthdayYear: parsedDate ? parsedDate.year() : null,
      birthdayMonth: parsedDate ? parsedDate.month() + 1 : null,
      birthdayDay: parsedDate ? parsedDate.date() : null,
      ...resolveFavoriteColorState(currentFavoriteColor),
      bestSubjects: currentBestSubject || [],
      accessory: currentAccessory || [],
      hairstyle: (currentHairstyle as EditorProfileState["hairstyle"]) || null,
      bodyType: (currentBodyType as EditorProfileState["bodyType"]) || null,
      faceShape: (currentFaceShape as EditorProfileState["faceShape"]) || null,
    };
  });

  // プロフィール状態更新
  const updateProfileState = useCallback((updates: Partial<EditorProfileState>) => {
    setProfileState((prev) => ({ ...prev, ...updates }));
  }, []);

  const {
    status: generationStatus,
    progress: generationProgress,
    partialImage,
    results: generationResults,
    generate,
    cancel: cancelGeneration,
    reset: resetGeneration,
  } = useAIImageGeneration({ uid: targetUid });

  const updateState = useCallback((updates: Partial<EditorState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  useEffect(() => {
    if (generationResults.length > 0 && generationStatus === GenerationStatus.COMPLETE) {
      updateState({ icon: generationResults[0].url });
    }
  }, [generationResults, generationStatus, updateState]);

  useEffect(() => {
    if (!open) {
      // モーダル閉じた時にリセット（IconSelectorと同じ）
      setSkippedToPreview(false);
      setProfileSubStep(ProfileSubStep.GENDER);
      setFavoritesSubStep(FavoritesSubStep.APPEARANCE);
      setAdditionalPrompt("");
      cancelGeneration();
      resetGeneration();
      return;
    }
    // 開いた時の処理
    setState({
      ...initialState,
      icon: "", // 空で初期化（placeholderを表示させる）
    });
    setCurrentStep(EditorStep.ICON_METHOD);
    setIsSubmitting(false);
    const parsedDate =
      currentBirthday && isValidBirthday(currentBirthday) ? dayjs(currentBirthday) : null;
    setProfileState({
      gender: (currentGender as EditorProfileState["gender"]) || null,
      birthday: parsedDate ? parsedDate.format("YYYY-MM-DD") : "",
      birthdayYear: parsedDate ? parsedDate.year() : null,
      birthdayMonth: parsedDate ? parsedDate.month() + 1 : null,
      birthdayDay: parsedDate ? parsedDate.date() : null,
      ...resolveFavoriteColorState(currentFavoriteColor),
      bestSubjects: currentBestSubject || [],
      accessory: currentAccessory || [],
      hairstyle: (currentHairstyle as EditorProfileState["hairstyle"]) || null,
      bodyType: (currentBodyType as EditorProfileState["bodyType"]) || null,
      faceShape: (currentFaceShape as EditorProfileState["faceShape"]) || null,
    });
  }, [open, cancelGeneration, resetGeneration]);

  const goToStep = useCallback(
    (step: EditorStep) => {
      setCurrentStep(step);

      // PROFILE_AI選択時の処理
      if (step === EditorStep.PROFILE_AI) {
        // プロフィールが完全な場合は直接生成画面へ
        if (isProfileComplete(profileState)) {
          setSkippedToPreview(true);
          setProfileSubStep(ProfileSubStep.ICON_PREVIEW);
          // 即座に生成を開始
          const age = profileState.birthday
            ? Math.floor(
                (new Date().getTime() - new Date(profileState.birthday).getTime()) /
                  (365.25 * 24 * 60 * 60 * 1000),
              )
            : undefined;

          generate({
            gender: (profileState.gender as "male" | "female" | "none") || "none",
            age,
            favorite_colors:
              profileState.favoriteColors.length > 0 ? profileState.favoriteColors : undefined,
            best_subjects:
              profileState.bestSubjects.length > 0 ? profileState.bestSubjects : undefined,
            accessory: profileState.accessory.length > 0 ? profileState.accessory : undefined,
            hairstyle: profileState.hairstyle || undefined,
            body_type: profileState.bodyType || undefined,
            face_shape: profileState.faceShape || undefined,
            self_introduction: currentSelfIntroduction || undefined,
            additional_prompt: additionalPrompt || undefined,
          });
        } else {
          // プロフィールが不完全な場合、最初の欠けている項目から開始
          setSkippedToPreview(false);
          const { profileSubStep: firstMissingStep, favoritesSubStep: firstMissingFavStep } =
            getFirstMissingStep(profileState);
          setProfileSubStep(firstMissingStep);
          setFavoritesSubStep(firstMissingFavStep || FavoritesSubStep.APPEARANCE);
        }
      }
    },
    [profileState, generate],
  );

  const canProceed = useCallback(() => {
    switch (currentStep) {
      case EditorStep.ICON_METHOD:
        return false;
      case EditorStep.PRESET_ICON:
        return !!state.icon;
      case EditorStep.UPLOAD_ICON:
        return !!state.icon;
      case EditorStep.PROFILE_AI:
        return generationStatus === GenerationStatus.COMPLETE && !!state.icon;
      default:
        return false;
    }
  }, [currentStep, state.icon, generationStatus]);

  const handleBack = useCallback(() => {
    play();
    btnDelay(() => {
      if (currentStep === EditorStep.PROFILE_AI) {
        // ProfileAI内のサブステップを戻る
        if (profileSubStep === ProfileSubStep.ICON_PREVIEW) {
          // 生成画面から戻る → FAVORITES(bestSubject)へ
          resetGeneration();
          setAdditionalPrompt("");
          setProfileSubStep(ProfileSubStep.FAVORITES);
          setFavoritesSubStep(FavoritesSubStep.BEST_SUBJECT);
        } else if (profileSubStep === ProfileSubStep.FAVORITES) {
          // FAVORITES内のサブステップを戻る: bestSubject → color → appearance → BIRTHDAY
          if (favoritesSubStep === FavoritesSubStep.BEST_SUBJECT) {
            setFavoritesSubStep(FavoritesSubStep.COLOR);
          } else if (favoritesSubStep === FavoritesSubStep.COLOR) {
            setFavoritesSubStep(FavoritesSubStep.APPEARANCE);
          } else {
            // appearance から戻る → BIRTHDAYへ
            setProfileSubStep(ProfileSubStep.BIRTHDAY);
          }
        } else if (profileSubStep === ProfileSubStep.BIRTHDAY) {
          setProfileSubStep(ProfileSubStep.GENDER);
        } else {
          // GENDERから戻る → ICON_METHOD画面へ
          setCurrentStep(EditorStep.ICON_METHOD);
          setAdditionalPrompt("");
        }
        return;
      }

      switch (currentStep) {
        case EditorStep.PRESET_ICON:
        case EditorStep.UPLOAD_ICON:
          setCurrentStep(EditorStep.ICON_METHOD);
          resetGeneration();
          break;
        default:
          break;
      }
    });
  }, [currentStep, profileSubStep, favoritesSubStep, play, resetGeneration]);

  // ProfileAI: サブステップ遷移（次へ）
  const handleProfileSubStepNext = useCallback(() => {
    play();
    btnDelay(() => {
      switch (profileSubStep) {
        case ProfileSubStep.GENDER:
          setProfileSubStep(ProfileSubStep.BIRTHDAY);
          break;
        case ProfileSubStep.BIRTHDAY:
          setProfileSubStep(ProfileSubStep.FAVORITES);
          setFavoritesSubStep(FavoritesSubStep.APPEARANCE);
          break;
        case ProfileSubStep.FAVORITES:
          // FavoritesSubStep内での遷移: appearance → color → bestSubject
          if (favoritesSubStep === FavoritesSubStep.APPEARANCE)
            setFavoritesSubStep(FavoritesSubStep.COLOR);
          else if (favoritesSubStep === FavoritesSubStep.COLOR)
            setFavoritesSubStep(FavoritesSubStep.BEST_SUBJECT);
          break;
        case ProfileSubStep.ICON_PREVIEW:
          // 生成完了後、アイコンを選択して確定
          break;
      }
    });
  }, [play, profileSubStep, favoritesSubStep]);

  // サブステップバーをクリックして遷移
  const handleSubStepClick = useCallback(
    (targetProfileSubStep: ProfileSubStep, targetFavoritesSubStep?: FavoritesSubStep) => {
      // 生成中はクリック無効
      if (generationStatus === GenerationStatus.GENERATING) {
        return;
      }
      play();
      btnDelay(() => {
        setProfileSubStep(targetProfileSubStep);
        if (targetFavoritesSubStep) {
          setFavoritesSubStep(targetFavoritesSubStep);
        }

        // アイコンステップがクリックされた場合、AI生成を開始
        if (targetProfileSubStep === ProfileSubStep.ICON_PREVIEW) {
          resetGeneration();
          const age = profileState.birthday
            ? Math.floor(
                (new Date().getTime() - new Date(profileState.birthday).getTime()) /
                  (365.25 * 24 * 60 * 60 * 1000),
              )
            : undefined;

          generate({
            gender: (profileState.gender as "male" | "female" | "none") || "none",
            age,
            favorite_colors:
              profileState.favoriteColors.length > 0 ? profileState.favoriteColors : undefined,
            best_subjects:
              profileState.bestSubjects.length > 0 ? profileState.bestSubjects : undefined,
            accessory: profileState.accessory.length > 0 ? profileState.accessory : undefined,
            hairstyle: profileState.hairstyle || undefined,
            body_type: profileState.bodyType || undefined,
            face_shape: profileState.faceShape || undefined,
            self_introduction: currentSelfIntroduction || undefined,
            additional_prompt: additionalPrompt || undefined,
          });
        }
      });
    },
    [
      play,
      profileState,
      generate,
      resetGeneration,
      generationStatus,
      currentSelfIntroduction,
      additionalPrompt,
    ],
  );

  // ProfileAI: AI生成開始
  const handleStartGeneration = useCallback(() => {
    play();
    btnDelay(async () => {
      setProfileSubStep(ProfileSubStep.ICON_PREVIEW);

      // プロフィール情報を先に保存
      try {
        await dispatch(
          updateUser({
            uid: targetUid,
            userInfo: {
              gender: profileState.gender,
              birthday: profileState.birthday,
              favorite_color: profileState.favoriteColors,
              best_subject: profileState.bestSubjects,
              accessory: profileState.accessory,
              hairstyle: profileState.hairstyle,
              body_type: profileState.bodyType,
              face_shape: profileState.faceShape,
            },
          }),
        ).unwrap();
      } catch (error) {
        console.error("Failed to save profile:", error);
      }

      const age = profileState.birthday
        ? Math.floor(
            (new Date().getTime() - new Date(profileState.birthday).getTime()) /
              (365.25 * 24 * 60 * 60 * 1000),
          )
        : undefined;

      generate({
        gender: (profileState.gender as "male" | "female" | "none") || "none",
        age,
        favorite_colors:
          profileState.favoriteColors.length > 0 ? profileState.favoriteColors : undefined,
        best_subjects: profileState.bestSubjects.length > 0 ? profileState.bestSubjects : undefined,
        accessory: profileState.accessory.length > 0 ? profileState.accessory : undefined,
        hairstyle: profileState.hairstyle || undefined,
        body_type: profileState.bodyType || undefined,
        face_shape: profileState.faceShape || undefined,
        self_introduction: currentSelfIntroduction || undefined,
        additional_prompt: additionalPrompt || undefined,
      });
    });
  }, [
    play,
    generate,
    profileState,
    currentSelfIntroduction,
    additionalPrompt,
    dispatch,
    targetUid,
  ]);

  // ProfileAI: 再生成
  const handleRegenerate = useCallback(() => {
    play();
    btnDelay(() => {
      resetGeneration();
      updateState({ icon: "" });
      handleStartGeneration();
    });
  }, [play, resetGeneration, updateState, handleStartGeneration]);

  // ProfileAI: 追加プロンプト付き再生成（IconSelectorと同じ実装）
  const handleRegenerateWithPrompt = useCallback(() => {
    resetGeneration();
    const age = profileState.birthday
      ? Math.floor(
          (new Date().getTime() - new Date(profileState.birthday).getTime()) /
            (365.25 * 24 * 60 * 60 * 1000),
        )
      : undefined;

    generate({
      gender: (profileState.gender as "male" | "female" | "none") || "none",
      age,
      favorite_colors:
        profileState.favoriteColors.length > 0 ? profileState.favoriteColors : undefined,
      best_subjects: profileState.bestSubjects.length > 0 ? profileState.bestSubjects : undefined,
      accessory: profileState.accessory.length > 0 ? profileState.accessory : undefined,
      hairstyle: profileState.hairstyle || undefined,
      body_type: profileState.bodyType || undefined,
      face_shape: profileState.faceShape || undefined,
      self_introduction: currentSelfIntroduction || undefined,
      additional_prompt: additionalPrompt || undefined,
    });
  }, [resetGeneration, generate, profileState, currentSelfIntroduction, additionalPrompt]);

  // 追加プロンプト変更ハンドラー
  const handleAdditionalPromptChange = useCallback((prompt: string) => {
    setAdditionalPrompt(prompt);
  }, []);

  const handleCancelGeneration = useCallback(() => {
    play();
    btnDelay(() => {
      cancelGeneration();
      resetGeneration();
      setSkippedToPreview(false);
      setAdditionalPrompt("");
      setCurrentStep(EditorStep.ICON_METHOD);
    });
  }, [play, cancelGeneration, resetGeneration]);

  const handleConfirm = useCallback(() => {
    // ProfileAI ICON_PREVIEW完了時はgeneratedImageを使用（IconSelectorと同じ）
    const generatedImage = generationResults.length > 0 ? generationResults[0].url : null;
    const iconToSelect =
      currentStep === EditorStep.PROFILE_AI && profileSubStep === ProfileSubStep.ICON_PREVIEW
        ? generatedImage
        : state.icon;

    if (!iconToSelect) return; // 早期リターン

    play();
    btnDelay(() => {
      setIsSubmitting(true);
      try {
        // アイコンのみを返す（プロフィールは生成開始時に保存済み）
        const profileData: ProfileData = {
          icon: iconToSelect,
        };
        onSelect(profileData);
        resetGeneration();
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    });
  }, [
    play,
    state.icon,
    currentStep,
    profileSubStep,
    generationResults,
    onSelect,
    onClose,
    resetGeneration,
  ]);

  const handleClose = useCallback(() => {
    play();
    btnDelay(() => {
      cancelGeneration();
      onClose();
    });
  }, [play, cancelGeneration, onClose]);

  // ヘッダー（現在のアイコン → 選択中のアイコン）
  const renderHeader = () => {
    if (currentStep === EditorStep.ICON_METHOD) {
      return null;
    }

    const displayIcon =
      currentStep === EditorStep.PROFILE_AI
        ? profileSubStep === ProfileSubStep.ICON_PREVIEW
          ? generationStatus === GenerationStatus.GENERATING
            ? null
            : generationResults.length > 0
              ? generationResults[0].url
              : partialImage
          : null
        : state.icon;

    return (
      <div className="flex-row-el items-center justify-center px-4">
        <div className="flex-row-el flex-center">
          {/* 現在のアイコン */}
          <div className="flex-col-el flex-center gap-1">
            {currentIcon ? (
              <UserAvatar size={100} icon={currentIcon} />
            ) : (
              <div
                className="flex-col-center bg-white rounded-full w-[100px] h-[100px]"
                style={{
                  boxShadow:
                    "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                }}
              >
                <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden bg-gray-10">
                  <span className="material-symbols-outlined text-[32px] text-gray-30">person</span>
                </div>
              </div>
            )}
            <p className="text-[16px] text-textcolor-black m-0">
              {I18n.t("MSG_ICON_CHANGE_PREVIEW_CURRENT_PARENT")}
            </p>
          </div>

          {/* 矢印 */}
          <div className="flex-col-el flex-center gap-1 shrink-0">
            <div className="w-[60px] h-[100px] flex-col-el flex-center">
              <span className="material-symbols-outlined text-[28px] text-gray-40">
                arrow_forward
              </span>
            </div>
            <p className="text-[16px] text-textcolor-black opacity-0 m-0">placeholder</p>
          </div>

          {/* 選択中のアイコン */}
          <div className="flex-col-el flex-center gap-1">
            {/* AI生成中: CircularProgress を表示 */}
            {currentStep === EditorStep.PROFILE_AI &&
            generationStatus === GenerationStatus.GENERATING ? (
              <div
                className="flex-col-center bg-white rounded-full w-[100px] h-[100px]"
                style={{
                  boxShadow:
                    "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                }}
              >
                <CircularProgress progress={generationProgress} size={84} />
              </div>
            ) : displayIcon ? (
              <UserAvatar size={100} icon={displayIcon} />
            ) : (
              <div
                className="flex-col-center bg-white rounded-full w-[100px] h-[100px]"
                style={{
                  boxShadow:
                    "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                }}
              >
                <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden bg-gray-10">
                  <span className="material-symbols-outlined text-[32px] text-gray-30">
                    add_photo_alternate
                  </span>
                </div>
              </div>
            )}
            <p className="text-[16px] text-textcolor-black m-0">
              {I18n.t("MSG_ICON_CHANGE_PREVIEW_SELECTED_PARENT")}
            </p>
          </div>
        </div>
      </div>
    );
  };

  const renderFooter = () => {
    // ProfileAIステップ用の動的フッター
    if (currentStep === EditorStep.PROFILE_AI) {
      const isGenerating = generationStatus === GenerationStatus.GENERATING;
      const isComplete = generationStatus === GenerationStatus.COMPLETE;

      // ICON_PREVIEW: 生成中はキャンセルボタンのみ
      if (profileSubStep === ProfileSubStep.ICON_PREVIEW && isGenerating) {
        return (
          <div className="flex-row-el flex-center gap-4 mt-6">
            <button
              onClick={handleCancelGeneration}
              className="flex-row-el items-center justify-center w-[200px] py-3 bg-danger text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all"
            >
              {I18n.t("MSG_CANCEL_BTN_BY_PARENT")}
            </button>
          </div>
        );
      }

      // ICON_PREVIEW: 生成完了後は戻る・確定ボタン
      if (profileSubStep === ProfileSubStep.ICON_PREVIEW) {
        return (
          <div className="flex-row-el flex-center gap-4 mt-6">
            {/* 戻るボタン - 通常フロー時のみ表示（直接PREVIEWに遷移した場合は非表示） */}
            {!skippedToPreview && (
              <button
                onClick={handleBack}
                className="flex-row-el items-center justify-center w-[200px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
              >
                {I18n.t("MSG_BACK_BTN_BY_PARENT")}
              </button>
            )}

            {/* 生成完了: これでいい！ボタン */}
            {isComplete && (
              <button
                onClick={handleConfirm}
                disabled={!state.icon}
                className="flex-row-el items-center justify-center w-[200px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {I18n.t("MSG_ICON_CONFIRM_BTN_BY_PARENT")}
              </button>
            )}
          </div>
        );
      }

      // FAVORITES (bestSubject): 戻る + 生成ボタン（最後のFAVORITESサブステップ）
      if (
        profileSubStep === ProfileSubStep.FAVORITES &&
        favoritesSubStep === FavoritesSubStep.BEST_SUBJECT
      ) {
        // プロフィール全体が完全かどうかをチェック（IconSelectorと同じ）
        const isGenerateDisabled = !isProfileComplete(profileState);
        return (
          <div className="flex-row-el flex-center gap-4 mt-6">
            <button
              onClick={handleBack}
              className="flex-row-el items-center justify-center w-[200px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
            >
              {I18n.t("MSG_BACK_BTN_BY_PARENT")}
            </button>
            <button
              onClick={handleStartGeneration}
              disabled={isGenerateDisabled}
              className="flex-row-el items-center justify-center w-[200px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {I18n.t("MSG_CREATE_USER_BTN_GENERATE_BY_PARENT")}
            </button>
          </div>
        );
      }

      // GENDER / BIRTHDAY / FAVORITES (appearance, color): 戻る + 次へボタン
      // 各ステップのバリデーション
      let isNextDisabled = false;
      if (profileSubStep === ProfileSubStep.GENDER) {
        // 性別は male または female のみ許可
        isNextDisabled = profileState.gender !== "male" && profileState.gender !== "female";
      } else if (profileSubStep === ProfileSubStep.BIRTHDAY) {
        isNextDisabled =
          profileState.birthdayYear === null ||
          profileState.birthdayMonth === null ||
          profileState.birthdayDay === null ||
          !isValidBirthday(profileState.birthday);
      } else if (
        profileSubStep === ProfileSubStep.FAVORITES &&
        favoritesSubStep === FavoritesSubStep.APPEARANCE
      ) {
        isNextDisabled =
          !profileState.accessory ||
          profileState.accessory.length === 0 ||
          !profileState.hairstyle ||
          !profileState.bodyType ||
          !profileState.faceShape;
      } else if (
        profileSubStep === ProfileSubStep.FAVORITES &&
        favoritesSubStep === FavoritesSubStep.COLOR
      ) {
        // 色は必須
        isNextDisabled = profileState.favoriteColors.length === 0;
      }

      return (
        <div className="flex-row-el flex-center gap-4 mt-6">
          <button
            onClick={handleBack}
            className="flex-row-el items-center justify-center w-[200px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
          >
            {I18n.t("MSG_BACK_BTN_BY_PARENT")}
          </button>
          <button
            onClick={handleProfileSubStepNext}
            disabled={isNextDisabled}
            className="flex-row-el items-center justify-center w-[200px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {I18n.t("MSG_NEXT_BTN_BY_PARENT")}
          </button>
        </div>
      );
    }

    // プリセット・アップロード選択後の確定ボタン
    const isConfirmStep =
      currentStep === EditorStep.PRESET_ICON || currentStep === EditorStep.UPLOAD_ICON;

    if (!isConfirmStep) {
      return null;
    }

    const nextDisabled = !canProceed();

    return (
      <div className="flex-row-el flex-center gap-4 mt-6">
        <button
          onClick={handleBack}
          className="flex-row-el items-center justify-center w-[200px] py-3 bg-white border border-gray2-60 text-textcolor-black rounded-[12px] text-[24px] cursor-pointer hover:bg-gray2-20 transition-all"
        >
          {I18n.t("MSG_BACK_BTN_BY_PARENT")}
        </button>
        <button
          onClick={handleConfirm}
          disabled={nextDisabled}
          className="flex-row-el items-center justify-center w-[200px] py-3 bg-ink text-white border-none rounded-[12px] text-[24px] cursor-pointer hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {I18n.t("MSG_CONFIRM_BTN_BY_PARENT")}
        </button>
      </div>
    );
  };

  const renderContent = () => {
    switch (currentStep) {
      case EditorStep.ICON_METHOD:
        return <IconMethodStep state={state} updateState={updateState} goToStep={goToStep} />;
      case EditorStep.PRESET_ICON:
        return <PresetIconStep state={state} updateState={updateState} />;
      case EditorStep.UPLOAD_ICON:
        return <UploadIconStep state={state} updateState={updateState} targetUid={targetUid} />;
      case EditorStep.PROFILE_AI:
        return (
          <ProfileAIStep
            state={profileState}
            updateState={updateProfileState}
            currentSubStep={profileSubStep}
            generationStatus={generationStatus}
            generationProgress={generationProgress}
            generatedImage={generationResults.length > 0 ? generationResults[0].url : null}
            partialImage={partialImage}
            favoritesSubStep={favoritesSubStep}
            onSubStepNext={handleProfileSubStepNext}
            onSubStepClick={handleSubStepClick}
            additionalPrompt={additionalPrompt}
            onAdditionalPromptChange={handleAdditionalPromptChange}
            onRegenerate={handleRegenerateWithPrompt}
            hideSubStepBar={skippedToPreview}
            completedStepIds={skippedToPreview ? undefined : getCompletedStepIds(profileState)}
            isGenerating={generationStatus === GenerationStatus.GENERATING}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog
      fullScreen
      open={open}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        {isSubmitting && <SpinModal />}

        <div
          className="flex-col-el flex-center relative"
          style={{
            width: PANEL_WIDTH * scale,
            height: PANEL_HEIGHT * scale,
          }}
        >
          <div
            className="flex-col-el shrink-0 border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30 relative"
            style={{
              width: PANEL_WIDTH,
              height: PANEL_HEIGHT,
              transform: `scale(${scale})`,
              filter:
                "drop-shadow(0px 6px 6px rgba(0, 0, 0, 0.2)) drop-shadow(0px 10px 14px rgba(0, 0, 0, 0.14))",
            }}
          >
            <div className="flex-col-el justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={handleClose} />
              {/* タイトル */}
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline shrink-0">
                <span className="material-symbols-outlined !text-[54px] text-red-100">edit</span>
                <h2 className="text-[33px] text-textcolor-black">
                  {I18n.t("MSG_PLAYER_ICON_EDITOR_TITLE")}
                </h2>
              </div>

              {currentStep === EditorStep.ICON_METHOD ? (
                <div className="flex-col-el w-full h-full overflow-hidden">{renderContent()}</div>
              ) : (
                <>
                  {renderHeader()}
                  <div className="flex-col-el w-full h-full overflow-hidden">{renderContent()}</div>
                  {renderFooter()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
